/**
 * Lloyd's coverholder bordereau mapping.
 *
 * Column headers follow the Lloyd's Coverholder Reporting Standards risk and
 * premium templates. Two deliberate choices worth knowing before a live
 * submission:
 *  - Dates are written as ISO 8601 (YYYY-MM-DD). Several broker templates use
 *    DD/MM/YYYY; ISO is unambiguous and sorts correctly, and the broker's data
 *    capture team should confirm which they want before the first submission.
 *  - `riskCode` / `classOfBusiness` default from the product but are overridable
 *    per export, because the binding authority contract — not the product — is
 *    the source of truth for the code that must be reported.
 */

import { STATUS_META } from '../PolicyAdmin/constants';
import {
  DEFAULT_CLASS_OF_BUSINESS,
  DEFAULT_RISK_CODE,
  PRODUCT_RISK_CODES,
  VALIDATION_SAMPLE_LIMIT,
} from './constants';
import type {
  BordereauBuild,
  BordereauColumn,
  BordereauRow,
  BordereauSettings,
  BordereauType,
  PolicyReportRecord,
  ValidationIssue,
  ValidationSeverity,
} from './types';
import { sanitizeFileName, toCsv, toMoneyString } from './utils';

const ALL: BordereauType[] = ['risk', 'premium', 'combined'];
const RISK_ONLY: BordereauType[] = ['risk', 'combined'];
const PREMIUM_ONLY: BordereauType[] = ['premium', 'combined'];

/** Days after the reporting period end that premium settles to the market. */
const SETTLEMENT_TERMS_DAYS = 45;

function addDays(dateStr: string, days: number): string {
  if (!dateStr) return '';
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

const riskCodeFor = (record: PolicyReportRecord, settings: BordereauSettings): string =>
  settings.riskCode || PRODUCT_RISK_CODES[record.product]?.riskCode || DEFAULT_RISK_CODE;

const classOfBusinessFor = (record: PolicyReportRecord, settings: BordereauSettings): string =>
  settings.classOfBusiness ||
  PRODUCT_RISK_CODES[record.product]?.classOfBusiness ||
  DEFAULT_CLASS_OF_BUSINESS;

const brokerageAmount = (record: PolicyReportRecord): number =>
  record.grossWrittenPremium * (record.brokerageRate / 100);

const commissionAmount = (record: PolicyReportRecord): number =>
  record.grossWrittenPremium * (record.coverholderCommissionRate / 100);

const totalDeductions = (record: PolicyReportRecord): number =>
  brokerageAmount(record) + commissionAmount(record);

const netPremium = (record: PolicyReportRecord): number =>
  record.grossWrittenPremium - totalDeductions(record);

const TRANSACTION_LABELS: Record<PolicyReportRecord['transactionType'], string> = {
  new: 'New',
  renewal: 'Renewal',
  endorsement: 'Endorsement',
  cancellation: 'Cancellation',
};

/**
 * The full column set. `sections` decides which bordereau types include a
 * column; `required` marks the fields Lloyd's will reject a submission for.
 */
export const BORDEREAU_COLUMNS: BordereauColumn[] = [
  // --- Contract identifiers -------------------------------------------------
  {
    key: 'umr',
    header: 'Unique Market Reference (UMR)',
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'Contract-level market reference for the binding authority.',
    value: (_r, s) => s.umr,
  },
  {
    key: 'brokerReference',
    header: 'Broker Reference',
    sections: ALL,
    required: true,
    format: 'text',
    hint: "The broker's own reference for the binder.",
    value: (_r, s) => s.brokerReference,
  },
  {
    key: 'coverholderName',
    header: 'Coverholder Name',
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'Approved coverholder entity writing the risk.',
    value: (_r, s) => s.coverholderName,
  },
  {
    key: 'coverholderPin',
    header: 'Coverholder PIN',
    sections: ALL,
    required: true,
    format: 'text',
    hint: "Lloyd's-issued coverholder identifier.",
    value: (_r, s) => s.coverholderPin,
  },
  {
    key: 'binderReference',
    header: 'Binding Authority Reference',
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'Reference of the binding authority the risk was written under.',
    value: (_r, s) => s.binderReference,
  },
  {
    key: 'yearOfAccount',
    header: 'Year of Account',
    sections: ALL,
    required: true,
    format: 'number',
    hint: 'Underwriting year the transaction is attributed to.',
    value: (r, s) => s.yearOfAccount || r.yearOfAccount,
  },
  {
    key: 'reportingPeriodEnd',
    header: 'Reporting Period End Date',
    sections: ALL,
    required: true,
    format: 'date',
    hint: 'Last day of the period this submission covers.',
    value: (_r, s) => s.periodEnd,
  },
  {
    key: 'section',
    header: 'Section Number',
    sections: ALL,
    required: false,
    format: 'text',
    hint: 'Section of the binder, where the contract is sectioned.',
    value: (_r, s) => s.section,
  },

  // --- Risk identity -------------------------------------------------------
  {
    key: 'riskReference',
    header: 'Risk Reference',
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'Coverholder policy number identifying the risk.',
    value: r => r.policyNumber,
  },
  {
    key: 'certificateReference',
    header: 'Certificate Reference',
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'Unique per transaction — must not repeat within a submission.',
    value: r => r.certificateReference,
  },
  {
    key: 'transactionType',
    header: 'Transaction Type',
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'New, Renewal, Endorsement or Cancellation.',
    value: r => TRANSACTION_LABELS[r.transactionType],
  },
  {
    key: 'originalInsured',
    header: 'Original Insured Name',
    sections: RISK_ONLY,
    required: true,
    format: 'text',
    hint: 'Legal name of the insured entity.',
    value: r => r.insuredName,
  },
  {
    key: 'insuredDomicile',
    header: 'Insured Domicile',
    sections: RISK_ONLY,
    required: true,
    format: 'text',
    hint: 'ISO 3166 alpha-2 country of the insured entity.',
    value: r => r.insuredDomicile,
  },
  {
    key: 'riskCity',
    header: 'Risk Location - City',
    sections: RISK_ONLY,
    required: false,
    format: 'text',
    hint: 'Primary location of the insured risk.',
    value: r => r.riskCity,
  },
  {
    key: 'riskState',
    header: 'Risk Location - State / Province',
    sections: RISK_ONLY,
    required: true,
    format: 'text',
    hint: 'Drives surplus lines tax allocation for US risks.',
    value: r => r.riskState,
  },
  {
    key: 'riskPostcode',
    header: 'Risk Location - Postcode',
    sections: RISK_ONLY,
    required: false,
    format: 'text',
    hint: 'Postal code of the primary risk location.',
    value: r => r.riskPostcode,
  },
  {
    key: 'riskCountry',
    header: 'Risk Location - Country',
    sections: RISK_ONLY,
    required: true,
    format: 'text',
    hint: 'ISO 3166 alpha-2 country of the risk location.',
    value: r => r.riskCountry,
  },
  {
    key: 'tradeOrIndustry',
    header: 'Trade or Industry',
    sections: RISK_ONLY,
    required: false,
    format: 'text',
    hint: 'Occupancy / trade description of the insured.',
    value: r => r.industry,
  },
  {
    key: 'sicCode',
    header: 'Industry Code (SIC)',
    sections: RISK_ONLY,
    required: false,
    format: 'text',
    hint: 'Standard Industrial Classification code.',
    value: r => r.sicCode,
  },
  {
    key: 'classOfBusiness',
    header: 'Class of Business',
    sections: RISK_ONLY,
    required: true,
    format: 'text',
    hint: 'Class as described in the binding authority.',
    value: (r, s) => classOfBusinessFor(r, s),
  },
  {
    key: 'riskCode',
    header: "Lloyd's Risk Code",
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'Risk code per the binder — confirm against the slip.',
    value: (r, s) => riskCodeFor(r, s),
  },

  // --- Period and exposure -------------------------------------------------
  {
    key: 'inceptionDate',
    header: 'Period of Insurance From',
    sections: ALL,
    required: true,
    format: 'date',
    hint: 'Inception date of the period of insurance.',
    value: r => r.effectiveDate,
  },
  {
    key: 'expiryDate',
    header: 'Period of Insurance To',
    sections: ALL,
    required: true,
    format: 'date',
    hint: 'Expiry date of the period of insurance.',
    value: r => r.expirationDate,
  },
  {
    key: 'writtenDate',
    header: 'Written Date',
    sections: ALL,
    required: true,
    format: 'date',
    hint: 'Date the coverholder bound the transaction.',
    value: r => r.writtenDate,
  },
  {
    key: 'cancellationDate',
    header: 'Cancellation Date',
    sections: ALL,
    required: false,
    format: 'date',
    hint: 'Populated only where coverage ended before expiry.',
    value: r => r.cancellationDate,
  },
  {
    key: 'policyStatus',
    header: 'Policy Status',
    sections: RISK_ONLY,
    required: false,
    format: 'text',
    hint: 'Coverholder-side lifecycle status, for reconciliation.',
    value: r => STATUS_META[r.status]?.label ?? r.status,
  },
  {
    key: 'livesCovered',
    header: 'Number of Lives Covered',
    sections: RISK_ONLY,
    required: false,
    format: 'number',
    hint: 'Insured headcount — the exposure base for this class.',
    value: r => r.employeeCount,
  },
  {
    key: 'benefitPercent',
    header: 'Benefit Percentage',
    sections: RISK_ONLY,
    required: false,
    format: 'percent',
    hint: 'Percentage of salary replaced during leave.',
    value: r => r.benefitPercent,
  },
  {
    key: 'weeklyCap',
    header: 'Weekly Benefit Cap',
    sections: RISK_ONLY,
    required: false,
    format: 'decimal',
    hint: 'Maximum weekly benefit per claimant.',
    value: r => r.weeklyCap,
  },
  {
    key: 'sumInsured',
    header: 'Sum Insured / Limit of Liability',
    sections: RISK_ONLY,
    required: true,
    format: 'decimal',
    hint: 'Aggregate limit in the original currency.',
    value: r => r.aggregateLimit,
  },
  {
    key: 'excess',
    header: 'Excess / Deductible (Days)',
    sections: RISK_ONLY,
    required: false,
    format: 'number',
    hint: 'Waiting period before benefits begin, in days.',
    value: r => r.waitingPeriodDays,
  },
  {
    key: 'signedLine',
    header: 'Signed Line Percentage',
    sections: ALL,
    required: true,
    format: 'percent',
    hint: 'Proportion of the risk written under this binder.',
    value: r => r.signedLinePercent,
  },

  // --- Premium and settlement ---------------------------------------------
  {
    key: 'originalCurrency',
    header: 'Original Currency',
    sections: ALL,
    required: true,
    format: 'text',
    hint: 'ISO 4217 currency the risk was written in.',
    value: (_r, s) => s.originalCurrency,
  },
  {
    key: 'settlementCurrency',
    header: 'Settlement Currency',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'text',
    hint: 'ISO 4217 currency premium settles in.',
    value: (_r, s) => s.settlementCurrency,
  },
  {
    key: 'rateOfExchange',
    header: 'Rate of Exchange',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'decimal',
    hint: 'Original to settlement currency rate. 1 where they match.',
    value: (_r, s) => s.rateOfExchange,
  },
  {
    key: 'grossPremium',
    header: 'Gross Premium (100%)',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'decimal',
    hint: 'Gross written premium before deductions. Negative on returns.',
    value: r => r.grossWrittenPremium,
  },
  {
    key: 'brokeragePercent',
    header: 'Brokerage Percentage',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'percent',
    hint: 'Broker commission rate applied to gross premium.',
    value: r => r.brokerageRate,
  },
  {
    key: 'brokerageAmount',
    header: 'Brokerage Amount',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'decimal',
    hint: 'Gross premium multiplied by the brokerage rate.',
    value: r => brokerageAmount(r),
  },
  {
    key: 'commissionPercent',
    header: 'Coverholder Commission Percentage',
    sections: PREMIUM_ONLY,
    required: false,
    format: 'percent',
    hint: 'Coverholder fee rate applied to gross premium.',
    value: r => r.coverholderCommissionRate,
  },
  {
    key: 'commissionAmount',
    header: 'Coverholder Commission Amount',
    sections: PREMIUM_ONLY,
    required: false,
    format: 'decimal',
    hint: 'Gross premium multiplied by the coverholder rate.',
    value: r => commissionAmount(r),
  },
  {
    key: 'totalDeductions',
    header: 'Total Deductions',
    sections: PREMIUM_ONLY,
    required: false,
    format: 'decimal',
    hint: 'Brokerage plus coverholder commission.',
    value: r => totalDeductions(r),
  },
  {
    key: 'netPremium',
    header: 'Net Premium',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'decimal',
    hint: 'Gross premium less total deductions.',
    value: r => netPremium(r),
  },
  {
    key: 'taxAmount',
    header: 'Tax / Statutory Charges',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'decimal',
    hint: 'Surplus lines tax, stamping and issuance fees.',
    value: r => r.taxesAndFees,
  },
  {
    key: 'settlementAmount',
    header: 'Premium Settlement Amount',
    sections: PREMIUM_ONLY,
    required: true,
    format: 'decimal',
    hint: 'Net premium converted at the rate of exchange.',
    value: (r, s) => netPremium(r) * (s.rateOfExchange || 1),
  },
  {
    key: 'instalmentIndicator',
    header: 'Instalment Premium Indicator',
    sections: PREMIUM_ONLY,
    required: false,
    format: 'text',
    hint: 'Y where premium is payable by instalments.',
    value: () => 'N',
  },
  {
    key: 'settlementDueDate',
    header: 'Settlement Due Date',
    sections: PREMIUM_ONLY,
    required: false,
    format: 'date',
    hint: `Reporting period end plus ${SETTLEMENT_TERMS_DAYS} days.`,
    value: (_r, s) => addDays(s.periodEnd, SETTLEMENT_TERMS_DAYS),
  },
];

export function columnsFor(type: BordereauType): BordereauColumn[] {
  return BORDEREAU_COLUMNS.filter(column => column.sections.includes(type));
}

/** Render one cell to the exact string that goes in the CSV. */
export function formatCell(
  value: string | number | null,
  format: BordereauColumn['format']
): string {
  if (value === null || value === undefined || value === '') return '';
  switch (format) {
    case 'decimal':
      return typeof value === 'number' ? toMoneyString(value) : String(value);
    case 'percent':
      return typeof value === 'number' ? value.toFixed(2) : String(value);
    case 'number':
      return typeof value === 'number' ? String(Math.round(value)) : String(value);
    case 'date':
    case 'text':
    default:
      return String(value);
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_CURRENCY = /^[A-Z]{3}$/;

interface IssueAccumulator {
  severity: ValidationSeverity;
  columnKey: string;
  columnHeader: string;
  message: string;
  rows: string[];
}

/**
 * Check a built bordereau against the rules Lloyd's applies on ingestion, plus
 * the internal-consistency checks that catch a bad export before it is sent.
 */
export function validateBordereau(
  rows: BordereauRow[],
  columns: BordereauColumn[],
  records: PolicyReportRecord[],
  settings: BordereauSettings
): ValidationIssue[] {
  const issues = new Map<string, IssueAccumulator>();

  const add = (
    severity: ValidationSeverity,
    column: Pick<BordereauColumn, 'key' | 'header'>,
    message: string,
    rowRef: string
  ) => {
    const id = `${severity}:${column.key}:${message}`;
    const existing = issues.get(id) ?? {
      severity,
      columnKey: column.key,
      columnHeader: column.header,
      message,
      rows: [],
    };
    existing.rows.push(rowRef);
    issues.set(id, existing);
  };

  const byKey = new Map(columns.map(column => [column.key, column]));
  const has = (key: string) => byKey.has(key);
  const certificateColumn = byKey.get('certificateReference');
  const seenCertificates = new Set<string>();

  rows.forEach((row, index) => {
    const record = records[index];
    const rowRef = String(
      row.certificateReference ?? record?.certificateReference ?? `row ${index + 1}`
    );

    columns.forEach(column => {
      const raw = row[column.key];
      const isBlank = raw === null || raw === undefined || String(raw).trim() === '';

      if (column.required && isBlank) {
        add('error', column, 'Mandatory field is empty', rowRef);
        return;
      }
      if (isBlank) return;

      if (column.format === 'date' && !ISO_DATE.test(String(raw))) {
        add('error', column, 'Date is not in YYYY-MM-DD format', rowRef);
      }
      if (
        (column.key === 'originalCurrency' || column.key === 'settlementCurrency') &&
        !ISO_CURRENCY.test(String(raw))
      ) {
        add('error', column, 'Currency must be a three-letter ISO 4217 code', rowRef);
      }
    });

    // Cross-field checks.
    if (certificateColumn) {
      const certificate = String(row.certificateReference ?? '');
      if (certificate && seenCertificates.has(certificate)) {
        add(
          'error',
          certificateColumn,
          'Duplicate certificate reference in this submission',
          rowRef
        );
      }
      seenCertificates.add(certificate);
    }

    if (has('inceptionDate') && has('expiryDate')) {
      const from = String(row.inceptionDate ?? '');
      const to = String(row.expiryDate ?? '');
      if (from && to && to <= from) {
        add(
          'error',
          byKey.get('expiryDate')!,
          'Expiry date is not after the inception date',
          rowRef
        );
      }
    }

    if (has('signedLine')) {
      const signed = Number(row.signedLine);
      if (!Number.isFinite(signed) || signed <= 0 || signed > 100) {
        add(
          'error',
          byKey.get('signedLine')!,
          'Signed line must be greater than 0 and at most 100',
          rowRef
        );
      }
    }

    if (has('rateOfExchange') && Number(row.rateOfExchange) <= 0) {
      add(
        'error',
        byKey.get('rateOfExchange')!,
        'Rate of exchange must be greater than zero',
        rowRef
      );
    }

    if (has('grossPremium') && Number(row.grossPremium) === 0) {
      add(
        'warning',
        byKey.get('grossPremium')!,
        'Zero gross premium — confirm this is a nil-premium transaction',
        rowRef
      );
    }

    if (has('sumInsured') && Number(row.sumInsured) <= 0) {
      add(
        'warning',
        byKey.get('sumInsured')!,
        'No limit of liability reported for the risk',
        rowRef
      );
    }

    if (settings.periodEnd && has('writtenDate')) {
      const written = String(row.writtenDate ?? '');
      if (written && written > settings.periodEnd) {
        add(
          'warning',
          byKey.get('writtenDate')!,
          'Written after the reporting period end — belongs in a later submission',
          rowRef
        );
      }
    }
  });

  // Contract-level checks that do not depend on a row.
  const umrColumn = byKey.get('umr');
  if (umrColumn && settings.umr && !/^B[0-9A-Z]{6,}$/i.test(settings.umr)) {
    add(
      'warning',
      umrColumn,
      'UMR does not look like a market reference (expected B followed by 6+ characters)',
      'all rows'
    );
  }

  return [...issues.values()].map(issue => ({
    severity: issue.severity,
    columnKey: issue.columnKey,
    columnHeader: issue.columnHeader,
    message: issue.message,
    sampleRows: [...new Set(issue.rows)].slice(0, VALIDATION_SAMPLE_LIMIT),
    affectedCount: new Set(issue.rows).size,
  }));
}

export function buildBordereau(
  records: PolicyReportRecord[],
  settings: BordereauSettings,
  type: BordereauType
): BordereauBuild {
  const columns = columnsFor(type);
  const rows: BordereauRow[] = records.map(record => {
    const row: BordereauRow = {};
    columns.forEach(column => {
      row[column.key] = column.value(record, settings);
    });
    return row;
  });

  return {
    columns,
    rows,
    issues: validateBordereau(rows, columns, records, settings),
    totals: {
      rowCount: rows.length,
      grossPremium: records.reduce((total, r) => total + r.grossWrittenPremium, 0),
      netPremium: records.reduce((total, r) => total + netPremium(r), 0),
      taxesAndFees: records.reduce((total, r) => total + r.taxesAndFees, 0),
    },
  };
}

export function bordereauToCsv(build: BordereauBuild, includeBom: boolean): string {
  const headers = build.columns.map(column => column.header);
  const body = build.rows.map(row =>
    build.columns.map(column => formatCell(row[column.key], column.format))
  );
  return toCsv(headers, body, includeBom);
}

/** e.g. Bordereau_Combined_B6118PARENTO26001_2026-07.csv */
export function bordereauFileName(type: BordereauType, settings: BordereauSettings): string {
  const label = type === 'combined' ? 'Combined' : type === 'risk' ? 'Risk' : 'Premium';
  const period = settings.periodEnd ? settings.periodEnd.slice(0, 7) : 'unscheduled';
  return sanitizeFileName(`Bordereau_${label}_${settings.umr || 'NoUMR'}_${period}.csv`);
}
